import type { ClientTelemetry } from '@teaplus/shared';

type Event = ClientTelemetry['events'][number];

const FLUSH_MS = 10_000;
const MAX_QUEUE = 25;
const MAX_ERRORS_PER_SESSION = 20;

const queue: Event[] = [];
let timer: number | null = null;
let errorsSent = 0;
const seenErrors = new Set<string>();

function send(keepalive = false) {
  if (timer !== null) {
    window.clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  const events = queue.splice(0, MAX_QUEUE);
  // Telemetry never gets in the way: failures are dropped.
  void fetch('/api/telemetry', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive,
    headers: { 'content-type': 'application/json', 'x-teaplus-request': '1' },
    body: JSON.stringify({ events }),
  }).catch(() => undefined);
}

/**
 * Reports what the server can't see for itself (pauses, buffering, player and app errors) for
 * the owner's admin panel. Batched every few seconds. Nothing here identifies the stream.
 */
export function track(e: Event) {
  queue.push(e);
  if (queue.length >= MAX_QUEUE) send();
  else if (timer === null) timer = window.setTimeout(() => send(), FLUSH_MS);
}

export function flushTelemetry() {
  send(true);
}

export function reportError(message: string, stack?: string, where?: string) {
  const key = `${message}|${where ?? ''}`;
  if (seenErrors.has(key) || errorsSent >= MAX_ERRORS_PER_SESSION) return;
  seenErrors.add(key);
  errorsSent++;
  track({
    kind: 'client_error',
    message: message.slice(0, 500),
    stack: stack?.slice(0, 2000),
    where: (where ?? location.pathname.replace(/\/[\w-]{12,}/g, '/:id')).slice(0, 120),
  });
}

let installed = false;
/** Catches errors nothing else handled, once per app start. */
export function installErrorReporting() {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (e) => {
    if (!e.error && !e.message) return;
    reportError(String(e.message || e.error), e.error instanceof Error ? e.error.stack : undefined);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    // Aborted requests and API errors are expected, and shown to the user already.
    if (r?.name === 'AbortError' || r?.code) return;
    reportError(
      r instanceof Error ? r.message : String(r),
      r instanceof Error ? r.stack : undefined,
    );
  });
  window.addEventListener('pagehide', flushTelemetry);
}
